// Getting the initiative back after it is gone.
//
// Reported 2026-10-01: losing the tracker is unrecoverable. A fight takes real
// time to assemble - scores rolled, tokens linked, aliases set, NPC conditions
// noted - and one mis-aimed Clear takes all of it with no way back. At a table
// with players waiting, rebuilding it is the worst five minutes of the session.
//
// A snapshot is the same set of keys the campaign switcher parks and unpacks,
// because that set already IS the answer to "what is the tracker right now".
// Reusing COMBAT_KEYS also means a key added for one is never forgotten by the
// other, which is how a half-restoring restore would have happened.
import { COMBAT_KEYS } from "./campaign";
import type { Layout } from "./types";

export interface InitiativeSnapshot {
  at: string;
  campaign: string;
  /** what it was, so the DM can see whether it is the one they want */
  rows: number;
  round: number;
  /** why it exists: the DM asked, or something was about to destroy it */
  reason: string;
  state: Record<string, unknown>;
}

/** How many snapshots are kept per campaign. Enough to undo a mistake made on
 *  top of a mistake, few enough that the layout does not grow without bound. */
export const SNAPSHOT_LIMIT = 5;

const rowsOf = (state: Record<string, unknown>) => (Array.isArray(state.externalInitiative) ? state.externalInitiative.length : 0);

/** The tracker as it stands, ready to be put back. */
export function takeSnapshot(layout: Layout, reason: string): InitiativeSnapshot {
  const state = Object.fromEntries(COMBAT_KEYS.map((k) => [k, layout[k]]));
  return {
    at: new Date().toISOString(),
    campaign: layout.activeCampaign || "",
    rows: rowsOf(state),
    round: Math.max(1, Number(state.roundNumber) || 1),
    reason,
    state,
  };
}

/** Every snapshot for a campaign, newest first. */
export function snapshotsFor(layout: Layout, campaign = layout.activeCampaign): InitiativeSnapshot[] {
  const all = (layout.initiativeSnapshots as InitiativeSnapshot[] | undefined) || [];
  return all.filter((s) => s && s.campaign === campaign);
}

/** Add a snapshot, newest first, trimming the oldest of THIS campaign only -
 *  another table's safety net is not ours to spend. */
export function withSnapshot(layout: Layout, snap: InitiativeSnapshot): Layout {
  const all = (layout.initiativeSnapshots as InitiativeSnapshot[] | undefined) || [];
  const mine = [snap, ...all.filter((s) => s && s.campaign === snap.campaign)].slice(0, SNAPSHOT_LIMIT);
  const others = all.filter((s) => s && s.campaign !== snap.campaign);
  return { ...layout, initiativeSnapshots: [...mine, ...others] };
}

/** Put a snapshot back.
 *
 *  Restoring is itself destructive - it replaces a tracker that might be the one
 *  the DM wanted - so the current state is snapshotted first. Undoing a restore
 *  is then the same button again, which is the only behaviour nobody has to be
 *  taught. */
export function restoreSnapshot(layout: Layout, snap: InitiativeSnapshot): Layout {
  const safety = takeSnapshot(layout, "replaced by a restore");
  const kept = withSnapshot(layout, safety);
  return { ...kept, ...snap.state };
}

/** Is there anything worth saving? An empty tracker snapshot is noise in the
 *  list and, worse, can push a real one off the end of it. */
export const worthSnapshotting = (layout: Layout): boolean => {
  const rows = layout.externalInitiative;
  return Array.isArray(rows) && rows.length > 0;
};

export function describeSnapshot(s: InitiativeSnapshot): string {
  const when = new Date(s.at);
  const time = Number.isNaN(when.getTime()) ? "unknown time" : when.toLocaleTimeString();
  return `${s.rows} row${s.rows === 1 ? "" : "s"} · round ${s.round} · ${time}${s.reason ? ` · ${s.reason}` : ""}`;
}
